import { ArrowDownIcon, ArrowUpIcon } from '@chakra-ui/icons';
import { Box, Checkbox, useToken } from '@chakra-ui/react';
import {
  type ColumnDef,
  type ColumnSizingInfoState,
  type ColumnSizingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type PaginationState,
  type RowData,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ReplotChartNames } from '../../constants';
import { plotTestId } from '../../utilities/plotTestId';
import { NoData } from '../plot-loader/plot-loader';
import styles from './table-chart.module.css';
import { TablePagination } from './table-pagination';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    isNumeric?: boolean;
  }
}

export type TableChartProps<TData extends object = object> = {
  data: TData[];
  columns?: ColumnDef<TData, unknown>[];
  searchTerm?: string;
  showZebraStripe?: boolean;
  zebraStripeColor?: string;
  isPaginated?: boolean;
  enableRowSelection?: boolean;
  onRowSelectionChange?: (selectedRows: TData[]) => void;
  selectedRows?: TData[];
  onSortedDataChange?: (sortedRows: TData[]) => void;
  boundaryRef?: React.RefObject<HTMLDivElement>;
  onRenderRow?: (params: {
    rowContent: ReactNode;
    rowData: TData;
    rowIndex: number;
    gridRef: HTMLDivElement | null;
  }) => ReactNode;
  onSelectRow?: (data: object) => void;
  className?: string;
  serverSidePagination?: {
    externalPaginationState: PaginationState;
    onPaginationChange: OnChangeFn<PaginationState>;
    totalItems: number;
    pageSizeOptions?: number[];
    hidePageSizeSelector?: boolean;
  };
  serverSideSorting?: {
    externalSortingState: SortingState;
    onSortingChange: OnChangeFn<SortingState>;
    onHeaderClick?: (columnId: string) => void;
  };
  controlledSorting?: {
    externalSortingState: SortingState;
    onSortingChange: OnChangeFn<SortingState>;
  };
  onSortingStateNotify?: (sorting: SortingState) => void;
  autoFitColumns?: boolean;
  rightAlignNumericColumns?: boolean;
};

export const TableChart = <TData extends object = object>({
  data,
  columns,
  searchTerm = '',
  showZebraStripe = true,
  isPaginated = false,
  enableRowSelection = false,
  onRowSelectionChange,
  selectedRows = [],
  onSortedDataChange,
  boundaryRef,
  onRenderRow,
  onSelectRow,
  className,
  serverSidePagination,
  serverSideSorting,
  controlledSorting,
  onSortingStateNotify,
  autoFitColumns = false,
  rightAlignNumericColumns = false,
}: TableChartProps<TData>) => {
  const zebraStripeColor = useToken('colors', 'lightBlue.50');

  const autoColumns = useMemo<ColumnDef<TData, unknown>[]>(() => {
    if (columns && columns.length > 0) {
      return enableRowSelection
        ? [
            {
              id: 'select',
              size: 34,
              enableSorting: false,
              enableResizing: false,
              cell: ({ row }) => (
                <Checkbox
                  aria-label="Select row"
                  isChecked={row.getIsSelected()}
                  onChange={row.getToggleSelectedHandler()}
                  colorScheme="green"
                  size="md"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                />
              ),
            },
            ...columns,
          ]
        : columns;
    }

    if (data.length === 0) return [];

    const base = Object.keys(data[0] as object)
      .filter((key) => !String(key).startsWith('_'))
      .map((key) => ({
        accessorKey: key,
        header: key,
        cell: (info) => String(info.getValue() ?? ''),
      })) as ColumnDef<TData, unknown>[];

    if (!enableRowSelection) return base;

    const selectCol: ColumnDef<TData, unknown> = {
      id: 'select',
      size: 34,
      enableSorting: false,
      enableResizing: false,
      cell: ({ row }) => (
        <Checkbox
          aria-label="Select row"
          isChecked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
          colorScheme="green"
          size="md"
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        />
      ),
    };

    return [selectCol, ...base];
  }, [columns, data, enableRowSelection]);

  const filteredData = useMemo<TData[]>(() => {
    if (!searchTerm) return data;
    const lower = searchTerm.toLowerCase();
    return data.filter((row) =>
      Object.values(row as object).some((value) =>
        String(value ?? '')
          .toLowerCase()
          .includes(lower)
      )
    );
  }, [data, searchTerm]);

  const numericColumnIds = useMemo<Set<string>>(() => {
    const ids = new Set<string>();
    for (const col of autoColumns) {
      if (col.meta?.isNumeric !== true) {
        continue;
      }
      const colId = (() => {
        if ('accessorKey' in col && typeof col.accessorKey === 'string') {
          return col.accessorKey;
        }
        if (col.id !== undefined) {
          return String(col.id);
        }
        return '';
      })();
      if (colId !== '') {
        ids.add(colId);
      }
    }
    return ids;
  }, [autoColumns]);

  const initialRowSelection = useMemo<RowSelectionState>(() => {
    if (!selectedRows || selectedRows.length === 0) return {};

    const selectionState: RowSelectionState = {};
    selectedRows.forEach((selectedRow) => {
      // biome-ignore lint/complexity/useIndexOf: migrated from develop
      const rowIndex = data.findIndex((dataRow) => dataRow === selectedRow);
      if (rowIndex !== -1) {
        selectionState[String(rowIndex)] = true;
      }
    });
    return selectionState;
  }, [selectedRows, data]);

  // Local pagination state used only when pagination is enabled (client-side)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 100,
  });

  const isServerSidePagination = !!serverSidePagination;
  const activePaginationState = isServerSidePagination
    ? serverSidePagination.externalPaginationState
    : pagination;
  const activePaginationSetter = isServerSidePagination
    ? serverSidePagination.onPaginationChange
    : setPagination;

  const [sorting, setSorting] = useState<SortingState>([]);

  const isServerSideSorting = !!serverSideSorting;
  const isControlledSorting = !isServerSideSorting && !!controlledSorting;

  const internalSortingSetterWithNotify = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      setSorting((old) =>
        typeof updater === 'function' ? updater(old) : updater
      );
    },
    []
  );

  let activeSortingState = sorting;
  let activeSortingSetter: OnChangeFn<SortingState> = setSorting;

  if (isControlledSorting && controlledSorting) {
    activeSortingState = controlledSorting.externalSortingState;
    activeSortingSetter = controlledSorting.onSortingChange;
  } else if (isServerSideSorting && serverSideSorting) {
    activeSortingState = serverSideSorting.externalSortingState;
    activeSortingSetter = serverSideSorting.onSortingChange;
  } else if (onSortingStateNotify) {
    activeSortingSetter = internalSortingSetterWithNotify;
  }

  useEffect(() => {
    if (!onSortingStateNotify || isControlledSorting || isServerSideSorting) {
      return;
    }
    onSortingStateNotify(sorting);
  }, [sorting, onSortingStateNotify, isControlledSorting, isServerSideSorting]);

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [columnSizingInfo, setColumnSizingInfo] =
    useState<ColumnSizingInfoState>({} as ColumnSizingInfoState);
  const [internalRowSelection, setInternalRowSelection] =
    useState<RowSelectionState>(initialRowSelection);

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = (updater) => {
    setInternalRowSelection((prev) =>
      typeof updater === 'function' ? updater(prev) : updater
    );
  };

  const prevSelectedRowsRef = useRef<TData[]>([]);

  useEffect(() => {
    const hasSelectionChanged =
      selectedRows.length !== prevSelectedRowsRef.current.length ||
      selectedRows.some((row, idx) => row !== prevSelectedRowsRef.current[idx]);

    if (hasSelectionChanged) {
      setInternalRowSelection(initialRowSelection);
      prevSelectedRowsRef.current = selectedRows;
    }
  }, [selectedRows, initialRowSelection]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    if (!onRowSelectionChange) return;
    const getSelected = () =>
      table.getSelectedRowModel().rows.map((r) => r.original as TData);
    onRowSelectionChange(getSelected());
  }, [internalRowSelection, onRowSelectionChange]);

  const table = useReactTable({
    data: filteredData,
    columns: autoColumns,
    state: {
      sorting: activeSortingState,
      columnSizing,
      columnSizingInfo,
      ...(isPaginated ? { pagination: activePaginationState } : {}),
      ...(enableRowSelection ? { rowSelection: internalRowSelection } : {}),
    },
    onSortingChange: activeSortingSetter,
    onColumnSizingChange: setColumnSizing,
    onColumnSizingInfoChange: setColumnSizingInfo,
    columnResizeMode: 'onChange',
    enableSorting: true,
    enableMultiSort: false,
    ...(isPaginated
      ? {
          onPaginationChange: activePaginationSetter,
          // For server-side pagination, enable manualPagination to prevent internal state management
          ...(isServerSidePagination
            ? { manualPagination: true }
            : { getPaginationRowModel: getPaginationRowModel() }),
        }
      : {}),
    ...(enableRowSelection
      ? {
          enableRowSelection: true,
          getRowId: (_row: TData, index: number) => String(index),
          onRowSelectionChange: handleRowSelectionChange,
        }
      : {}),
    ...(isServerSideSorting
      ? { manualSorting: true }
      : { getSortedRowModel: getSortedRowModel() }),
    getCoreRowModel: getCoreRowModel(),
  });

  const isEmpty = filteredData.length === 0;
  const rows = table.getRowModel().rows;
  // With getRowId: String(index), row.id is display order not stable identity; compare original refs to detect reorders.
  const prevSortedOriginalsRef = useRef<TData[]>([]);

  useEffect(() => {
    if (!onSortedDataChange) return;
    const nextSortedRows = rows.map((row) => row.original);
    const isSameOrder =
      prevSortedOriginalsRef.current.length === nextSortedRows.length &&
      prevSortedOriginalsRef.current.every(
        (row, index) => row === nextSortedRows[index]
      );

    if (isSameOrder) return;

    prevSortedOriginalsRef.current = nextSortedRows;
    onSortedDataChange(nextSortedRows);
  }, [rows, onSortedDataChange]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 38,
    overscan: 20,
  });

  const getZebraStripeStyles = (index: number) => {
    if (!showZebraStripe || index % 2 !== 0 || !zebraStripeColor) {
      return {};
    }
    return { '--zebra-stripe-color': zebraStripeColor };
  };

  const getRowClassName = (index: number, isSelected: boolean) => {
    return classNames(
      showZebraStripe && index % 2 === 0 ? styles.rowEven : styles.rowOdd,
      enableRowSelection && isSelected && styles.rowSelected
    );
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      w="100%"
      h="100%"
      position="relative"
      data-testid={plotTestId(ReplotChartNames.TableChart)}
      ref={boundaryRef}
      className={className}
    >
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        overflow="hidden"
        className={classNames(styles.containerBorder)}
      >
        {isEmpty ? (
          <NoData />
        ) : (
          <Box
            ref={scrollRef}
            flex={1}
            overflow="auto"
            className={styles.stableScrollbarGutter}
          >
            <div style={{ height: `${virtualizer.getTotalSize()}px` }}>
              <table className={styles.table}>
                <thead className={styles.stickyHeader}>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header, hIdx) => (
                        <th
                          key={header.id}
                          onClick={(() => {
                            if (columnSizingInfo.isResizingColumn) {
                              return undefined;
                            }
                            if (serverSideSorting?.onHeaderClick) {
                              return () =>
                                serverSideSorting.onHeaderClick?.(
                                  header.column.id
                                );
                            }
                            return header.column.getToggleSortingHandler();
                          })()}
                          aria-sort={(():
                            | 'ascending'
                            | 'descending'
                            | 'none' => {
                            const sorted = header.column.getIsSorted();
                            if (sorted === 'asc') return 'ascending';
                            if (sorted === 'desc') return 'descending';
                            return 'none';
                          })()}
                          className={classNames(
                            styles.headerCell,
                            header.column.getCanSort()
                              ? styles.headerCellClickable
                              : styles.headerCellNotClickable,
                            hIdx === 0 && styles.firstCol
                          )}
                          style={{
                            ...(autoFitColumns
                              ? {}
                              : {
                                  width: `${header.column.getSize()}px`,
                                  maxWidth: `${header.column.getSize()}px`,
                                }),
                            ...(rightAlignNumericColumns &&
                            numericColumnIds.has(header.column.id)
                              ? { textAlign: 'right' as const }
                              : {}),
                          }}
                        >
                          <span
                            className={styles.headerContent}
                            style={
                              rightAlignNumericColumns &&
                              numericColumnIds.has(header.column.id)
                                ? {
                                    justifyContent: 'flex-end',
                                    width: '100%',
                                  }
                                : undefined
                            }
                          >
                            <span className={styles.headerText}>
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                            </span>
                            {header.column.getCanSort() &&
                              header.column.getIsSorted() && (
                                <span className={styles.sortIcon}>
                                  {header.column.getIsSorted() === 'asc' ? (
                                    <ArrowUpIcon boxSize={3} />
                                  ) : (
                                    <ArrowDownIcon boxSize={3} />
                                  )}
                                </span>
                              )}
                          </span>
                          {header.column.getCanResize() && (
                            <div
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              onDoubleClick={(e) => {
                                e.stopPropagation();
                              }}
                              className={styles.resizer}
                            />
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {virtualizer.getVirtualItems().map((virtualRow, index) => {
                    const row = rows[virtualRow.index];
                    if (!row) return null;
                    const isLastRow = virtualRow.index === rows.length - 1;

                    const defaultRowContent = (
                      <tr
                        key={row.id}
                        className={getRowClassName(
                          virtualRow.index,
                          row.getIsSelected()
                        )}
                        style={{
                          height: `${virtualRow.size}px`,
                          transform: `translateY(${virtualRow.start - index * virtualRow.size}px)`,
                          cursor: 'pointer',
                          ...getZebraStripeStyles(virtualRow.index),
                        }}
                      >
                        {row.getVisibleCells().map((cell, cellIdx) => (
                          <td
                            key={cell.id}
                            className={classNames(
                              styles.cell,
                              cellIdx === 0 && styles.firstCol,
                              isLastRow && styles.lastRowCell
                            )}
                            style={{
                              ...(autoFitColumns
                                ? {}
                                : {
                                    width: `${cell.column.getSize()}px`,
                                    maxWidth: `${cell.column.getSize()}px`,
                                  }),
                              ...(rightAlignNumericColumns &&
                              numericColumnIds.has(cell.column.id)
                                ? { textAlign: 'right' as const }
                                : {}),
                            }}
                            onClick={() => {
                              onSelectRow?.(row.original);
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    );

                    if (!onRenderRow) return defaultRowContent;

                    return onRenderRow({
                      rowContent: defaultRowContent,
                      rowData: row.original,
                      rowIndex: virtualRow.index,
                      gridRef: scrollRef.current,
                    });
                  })}
                </tbody>
              </table>
            </div>
          </Box>
        )}
      </Box>

      {isPaginated && (
        <TablePagination
          pagination={activePaginationState}
          setPagination={activePaginationSetter}
          totalItems={
            isServerSidePagination
              ? serverSidePagination.totalItems
              : filteredData.length
          }
          pageSizeOptions={serverSidePagination?.pageSizeOptions}
          hidePageSizeSelector={
            serverSidePagination?.hidePageSizeSelector ?? false
          }
        />
      )}
    </Box>
  );
};
